import { createServerFn } from "@tanstack/react-start"
import { Result } from "@praha/byethrow"
import { addPost as addPostService, getPostsByTopic as getPostsByTopicService } from "@apps/gwservice/posts"
import { getTopic as getTopicService, getTopicContent as getTopicContentService } from "@apps/gwservice/topics"
import { withAuth } from "~/features/auth/server/auth"
import { APP_UPLOAD_LIMIT_ERROR, isFileWithinAppLimit } from "~/lib/upload-limit"

export const getTopic = createServerFn({
  method: "GET",
})
  .inputValidator((data: { id: number }) => data)
  .handler(({ data }) =>
    Result.try({
      try: () => withAuth(getTopicService)(data.id),
      catch: () => "unexpected server error",
    }),
  )

export const getTopicContent = createServerFn({
  method: "GET",
})
  .inputValidator((data: { id: number }) => data)
  .handler(({ data }) => withAuth(getTopicContentService)(data.id))

export const getPostsByTopic = createServerFn({
  method: "GET",
})
  .inputValidator((data: { topicId: number; page?: number }) => data)
  .handler(({ data }) =>
    Result.try({
      try: () => withAuth(getPostsByTopicService)(data.topicId, data.page),
      catch: () => "unexpected server error",
    }),
  )

type AddPostInput = {
  topicId: number
  text: string
  file?: File
}

export const addPostCodec = {
  toFormData: (input: AddPostInput) => {
    const data = new FormData()

    data.set("topicId", String(input.topicId))
    data.set("text", input.text)
    if (input.file) data.set("file", input.file)

    return { data }
  },
  fromFormData: (data: FormData): AddPostInput => {
    const file = data.get("file")

    return {
      topicId: Number(data.get("topicId")),
      text: String(data.get("text") ?? ""),
      file: file instanceof File ? file : undefined,
    }
  },
}

export const addPost = createServerFn({
  method: "POST",
})
  .inputValidator((data: FormData) => {
    const input = addPostCodec.fromFormData(data)
    // TanStack Start inputValidator host contract: throw to reject input
    if (input.file && !isFileWithinAppLimit(input.file)) throw new Error(APP_UPLOAD_LIMIT_ERROR)
    return input
  })
  .handler(({ data }) =>
    Result.try({
      try: () => withAuth(addPostService)(data.topicId, data.text, data.file),
      catch: () => "unexpected server error",
    }),
  )
